import { useState, useEffect, useRef } from 'react';
import { X, Copy, Send } from 'lucide-react';
import { addLinks } from '../../../apiServices/linkApi';
import { sendEmails, sendEmailsWithMessage, emailSendError } from '../../../apiServices/emailApi';
import { useNotification } from '@/hooks/useNotification';
import { appUrl } from '@/lib/appUrl';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Spinner } from '../../ui/Spinner';

// ── Utility ─────────────────────────────────────────────────────────

function getIdsList(selectedFiles: Array<{ id: string }>, modalParams?: Array<{ selectedFiles: Array<{ id: string }> }>) {
  if (selectedFiles.length > 0) return selectedFiles.map((f) => f.id);
  if (modalParams?.[0]) return [modalParams[0].selectedFiles[0].id];
  return [];
}

function getSenderName(currentUserInfo: Record<string, unknown> | undefined): string {
  if (!currentUserInfo) return '';
  const attrs = (currentUserInfo as { cognitoAttributes?: { UserAttributes?: Array<{ Name: string; Value: string }> } })
    .cognitoAttributes?.UserAttributes;
  if (!attrs) return '';
  const firstName = attrs.find((a) => a.Name === 'given_name')?.Value || '';
  const lastName = attrs.find((a) => a.Name === 'family_name')?.Value || '';
  return `${firstName} ${lastName}`.trim();
}

import { validateEmailFormat } from '../../../util/UtilFunctions';

// ── Component ───────────────────────────────────────────────────────

interface ShareFileModalProps {
  selectedFiles: Array<{ id: string; name?: string }>;
  modalParams?: unknown[];
  currentUserInfo?: Record<string, unknown>;
  updateIsQuerying: (querying: boolean) => void;
  closeModal: () => void;
}

export default function ShareFileModal({
  selectedFiles,
  modalParams,
  currentUserInfo,
  updateIsQuerying,
  closeModal,
}: ShareFileModalProps) {
  const { notify } = useNotification();
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(true);
  const linkInputRef = useRef<HTMLInputElement>(null);

  // Email state
  const [emailInput, setEmailInput] = useState('');
  const [confirmedEmails, setConfirmedEmails] = useState<string[]>([]);
  const [messageText, setMessageText] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Generate share link on mount
  useEffect(() => {
    async function fetchLink() {
      try {
        const idsList = getIdsList(selectedFiles, modalParams as Parameters<typeof getIdsList>[1]);
        if (!idsList.length) return;
        const response = await addLinks(idsList);
        setLinkUrl(appUrl(`/in/link/${(response as { response: string }).response}`));
      } catch {
        notify({ type: 'error', message: 'Could not generate share link' });
      } finally {
        setLinkLoading(false);
      }
    }
    fetchLink();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyLink = () => {
    if (!linkUrl) return;
    navigator.clipboard.writeText(linkUrl);
    notify({ type: 'success', message: 'Link copied' });
  };

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) { setEmailError('No email address given'); return; }
    if (!validateEmailFormat(trimmed)) { setEmailError('Not a valid email address'); return; }
    if (confirmedEmails.includes(trimmed)) { setEmailError('Email already added'); return; }
    setConfirmedEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
    setEmailError('');
  };

  const removeEmail = (email: string) => {
    setConfirmedEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  };

  const handleSendEmail = async () => {
    if (emailInput.trim()) { addEmail(); return; }
    if (confirmedEmails.length === 0) return;
    if (!linkUrl) { notify({ type: 'info', message: 'No link to send' }); return; }

    setEmailSending(true);
    try {
      updateIsQuerying(true);
      const senderName = getSenderName(currentUserInfo);
      const res = messageText.trim()
        ? await sendEmailsWithMessage(confirmedEmails, senderName, linkUrl, messageText)
        : await sendEmails(confirmedEmails, senderName, linkUrl);
      updateIsQuerying(false);
      const error = emailSendError(res);
      if (error) {
        // Keep the dialog open so the recipients and message aren't lost
        setEmailError(error);
        return;
      }
      notify({ type: 'success', message: 'Email sent' });
      closeModal();
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: 'Could not send email' });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Copy link section */}
      <div className="space-y-2">
        <h5 className="text-sm font-medium text-muted-foreground">Copy this link to share file</h5>
        {linkLoading ? (
          <div className="flex h-10 items-center justify-center"><Spinner size="sm" /></div>
        ) : linkUrl ? (
          <div className="flex gap-2">
            <Input ref={linkInputRef} value={linkUrl} readOnly className="flex-1" />
            <Button variant="secondary" onClick={handleCopyLink} className="shrink-0">
              <Copy className="mr-1.5 size-4" /> Copy
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Could not generate link</p>
        )}
      </div>

      {/* Send email section */}
      <div className="space-y-3">
        <h5 className="text-sm font-medium text-muted-foreground">Send link to email address</h5>

        {confirmedEmails.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {confirmedEmails.map((email) => (
              <li key={email} className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm">
                {email}
                <button type="button" onClick={() => removeEmail(email)} className="text-muted-foreground hover:text-destructive">
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Email"
            value={emailInput}
            onChange={(e) => { setEmailInput(e.target.value.toLowerCase()); setEmailError(''); }}
            onKeyDown={handleEmailKeyDown}
            className="flex-1"
          />
          <Button variant="secondary" onClick={addEmail} className="shrink-0">Add</Button>
        </div>
        {emailError && <p className="text-xs text-destructive">{emailError}</p>}

        <textarea
          placeholder="Message (optional)"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          className="w-full resize-none rounded-md border border-border p-2 text-sm outline-none placeholder:text-muted-foreground focus:border-ring"
          rows={3}
        />

        <Button onClick={handleSendEmail} disabled={emailSending || confirmedEmails.length === 0}>
          {emailSending ? <Spinner size="sm" /> : <Send className="mr-1.5 size-4" />}
          Send email
        </Button>
      </div>
    </div>
  );
}
